/**
 * HttpWsTransport.ts — talks to the real Go backend (base /v1).
 *
 * Split of responsibility (matches the architecture):
 *   - PICTURES ARE COSMETIC (carry no EV) -> the client owns the catalog + geometry locally
 *     (same library the mock uses). getPictures/selectPicture are served locally; selectPicture
 *     also REGISTERS the picture's per-color cell counts with the server so its drip schedule
 *     length matches what the client renders.
 *   - MONEY IS SERVER-AUTHORITATIVE -> session, buy, tap, balance, picture-complete and fairness
 *     all go to the Go server. Taps are intent requests (POST /tap); the server pre-rolled the
 *     outcome at buy time and streams authoritative reveal events back over SSE.
 *
 * Realtime uses SSE (EventSource) — zero-dependency and matches the reference server. Client->server
 * taps are POST /tap. (Production may swap SSE+POST for a WebSocket with the identical schema.)
 */
import { apiBase } from './Transport';
import type { Transport } from './Transport';
import { BET_LEVELS } from './mathConfig';
import { getPictureById, getPictureLibrary } from '../pictures/library';
import type {
  AutoplayRequest,
  AutoplayResponse,
  AutoplayStopRequest,
  BuyRequest,
  BuyResponse,
  CellDef,
  ClientWsMessage,
  FairnessResponse,
  PaletteEntry,
  PictureInstanceResponse,
  PicturesResponse,
  PictureSummary,
  RotateSeedResponse,
  ServerWsMessage,
  SessionRequest,
  SessionResponse,
} from './contract';

function countByColor(cells: { colorId: number }[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const c of cells) m.set(c.colorId, (m.get(c.colorId) ?? 0) + 1);
  return m;
}

export class HttpWsTransport implements Transport {
  readonly kind = 'http' as const;
  private base: string;
  private v1: string;
  private sessionId: string | null = null;
  private token: string | null = null;
  private es: EventSource | null = null;
  private listeners = new Set<(m: ServerWsMessage) => void>();

  constructor(base = apiBase()) {
    this.base = base.replace(/\/$/, '');
    this.v1 = `${this.base}/v1`;
  }

  private authHeaders(): Record<string, string> {
    const h: Record<string, string> = { 'content-type': 'application/json' };
    if (this.token) h['authorization'] = `Bearer ${this.token}`;
    return h;
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${this.v1}${path}`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify(body ?? {}),
    });
    if (!res.ok) throw new Error(`${path} -> ${res.status}`);
    return (await res.json()) as T;
  }

  private async get<T>(path: string): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) headers['authorization'] = `Bearer ${this.token}`;
    const res = await fetch(`${this.v1}${path}`, { headers });
    if (!res.ok) throw new Error(`${path} -> ${res.status}`);
    return (await res.json()) as T;
  }

  async createSession(req: SessionRequest): Promise<SessionResponse> {
    const r = await this.post<SessionResponse>('/session', req);
    this.sessionId = r.sessionId;
    this.token = r.token ?? null; // bearer for every subsequent call
    return r;
  }

  // --- cosmetic, served locally from the picture library (same as the mock) ---
  async getPictures(): Promise<PicturesResponse> {
    const pictures: PictureSummary[] = getPictureLibrary().map((def) => {
      const counts = countByColor(def.cells);
      const palette: PaletteEntry[] = def.palette.map((p) => ({
        colorId: p.colorId,
        number: p.number,
        hex: p.hex,
        cellCount: counts.get(p.colorId) ?? 0,
      }));
      const stakeIdx =
        def.sizeName === 'large' ? [3, 5, 6] : def.sizeName === 'medium' ? [2, 3, 4] : [1, 2, 3];
      return {
        pictureId: def.id,
        title: def.title,
        thumbnailUrl: '',
        palette,
        totalCells: def.cells.length,
        recommendedStakes: stakeIdx.map((i) => BET_LEVELS[i]),
        sizeName: def.sizeName,
        tags: def.tags ?? [],
        sourceImage: def.sourceImage,
      };
    });
    return { pictures };
  }

  async selectPicture(pictureId: string): Promise<PictureInstanceResponse> {
    const def = getPictureById(pictureId);
    if (!def) throw new Error('unknown picture');
    const counts = countByColor(def.cells);
    const palette: PaletteEntry[] = def.palette.map((p) => ({
      colorId: p.colorId,
      number: p.number,
      hex: p.hex,
      cellCount: counts.get(p.colorId) ?? 0,
    }));
    const cells: CellDef[] = def.cells.map((c) => ({
      cellId: c.cellId,
      colorId: c.colorId,
      cx: c.cx,
      cy: c.cy,
      labelR: c.labelR,
      poly: c.poly,
    }));
    // Register the (cosmetic) per-color cell counts with the server so its drip schedule
    // length matches the client's geometry. Money stays server-authoritative.
    await this.post(`/session/${this.sid()}/picture`, { pictureId, colors: palette });
    return { pictureInstanceId: 'pi-' + pictureId, palette, cells };
  }

  // --- money: server-authoritative ---
  buyColor(req: BuyRequest): Promise<BuyResponse> {
    return this.post<BuyResponse>(`/session/${this.sid()}/buy`, req);
  }

  startAutoplay(req: AutoplayRequest): Promise<AutoplayResponse> {
    return this.post<AutoplayResponse>(`/session/${this.sid()}/autoplay`, req);
  }

  stopAutoplay(req: AutoplayStopRequest): Promise<AutoplayResponse> {
    return this.post<AutoplayResponse>(`/session/${this.sid()}/autoplay/stop`, req);
  }

  rotateSeed(): Promise<RotateSeedResponse> {
    return this.post<RotateSeedResponse>(`/session/${this.sid()}/rotate-seed`, {});
  }

  getFairness(betId: string): Promise<FairnessResponse> {
    return this.get<FairnessResponse>(`/fairness/${betId}`);
  }

  // --- realtime: SSE downstream, POST /tap upstream ---
  async connect(): Promise<void> {
    if (this.es && this.es.readyState !== EventSource.CLOSED) return; // idempotent
    // EventSource can't set headers, so the token rides in the query string
    const q = this.token ? `?token=${encodeURIComponent(this.token)}` : '';
    const url = `${this.v1}/session/${this.sid()}/stream${q}`;
    await new Promise<void>((resolve) => {
      const es = new EventSource(url);
      this.es = es;
      es.onopen = () => resolve();
      es.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as ServerWsMessage;
          this.listeners.forEach((l) => l(msg));
        } catch {
          /* ignore keep-alives / malformed */
        }
      };
      // resolve even if onopen is slow so the scene can proceed; events still stream once open
      setTimeout(resolve, 400);
    });
  }

  send(msg: ClientWsMessage): void {
    if (msg.t === 'tap_cell') {
      void this.post(`/session/${this.sid()}/tap`, { betId: msg.betId, cellIndex: msg.cellIndex });
    } else if (msg.t === 'autoplay_ctrl' && msg.action === 'stop') {
      void this.post(`/session/${this.sid()}/autoplay/stop`, { autoplayRunId: msg.autoplayRunId });
    }
    // 'resync' is a no-op against the reference server (it replays idempotently on next tap)
  }

  onMessage(fn: (m: ServerWsMessage) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  dispose(): void {
    this.es?.close();
    this.es = null;
    this.listeners.clear();
  }

  private sid(): string {
    if (!this.sessionId) throw new Error('no session');
    return this.sessionId;
  }
}
